import React, { useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBoxArchive,
  faBoxOpen,
  faEdit,
  faThumbTack,
  faUnlockAlt,
} from '@fortawesome/free-solid-svg-icons'
import useClickAway from 'react-use/lib/useClickAway'
import useKey from 'react-use/lib/useKey'

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  z-index: 1000;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease;
`

const DropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;

  &:hover {
    background: #f5f5f5;
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }

  &:only-child {
    border-radius: 6px;
  }
`

const DropdownItemIcon = styled.div`
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

interface ChatDropdownProps {
  isOpen: boolean
  isFixed?: boolean
  onClose: () => void
  onFix?: () => void
  onRename?: () => void
  onArchive?: () => void
  onRestore?: () => void
  anchorElement: HTMLElement | null
}

export default function ChatDropdown({
  isOpen,
  isFixed = false,
  onFix,
  onClose,
  onRename,
  onArchive,
  onRestore,
  anchorElement,
}: ChatDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useClickAway(dropdownRef, () => {
    onClose()
  })

  useKey('Escape', () => {
    onClose()
  })

  // Função para calcular a posição ideal do dropdown
  const calculatePosition = useMemo(() => {
    if (!anchorElement || !isOpen) return { top: 0, left: 0 }

    const anchorRect = anchorElement.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Estimativa do tamanho do dropdown (altura baseada no número de itens)
    const itemCount = [onFix, onRename, onArchive].filter(Boolean).length
    const estimatedHeight = itemCount * 40
    const estimatedWidth = 140

    let top = anchorRect.top + anchorRect.height
    let left = anchorRect.left + 15

    // Verificar se o dropdown vai sair da tela na horizontal
    if (left + estimatedWidth > viewportWidth) {
      // Posicionar à esquerda do anchor
      left = anchorRect.right - estimatedWidth - 5
    }

    // Se ainda estiver saindo da tela à esquerda, ajustar para a margem mínima
    if (left < 10) {
      left = 10
    }

    // Verificar se o dropdown vai sair da tela na vertical
    if (top + estimatedHeight > viewportHeight) {
      // Posicionar acima do anchor
      top = anchorRect.top - estimatedHeight - 2
    }

    // Se ainda estiver saindo da tela por cima, ajustar para a margem mínima
    if (top < 10) {
      top = 10
    }

    return { top, left }
  }, [anchorElement, isOpen, onFix, onRename, onArchive])

  const dropdownStyle = useMemo(() => {
    if (!anchorElement || !isOpen) return {}

    const position = calculatePosition

    return {
      top: position.top,
      left: position.left,
      right: 'auto',
      position: 'fixed',
      zIndex: 1000,
    }
  }, [anchorElement, isOpen])

  const dropdownContent = isOpen ? (
    <DropdownMenu ref={dropdownRef} $isOpen={isOpen} style={dropdownStyle as React.CSSProperties}>
      {onFix && (
        <DropdownItem onClick={onFix}>
          <DropdownItemIcon>
            <FontAwesomeIcon
              icon={isFixed ? faUnlockAlt : faThumbTack}
              style={isFixed ? {} : { transform: 'rotate(45deg)' }}
            />
          </DropdownItemIcon>
          {isFixed ? 'Desafixar' : 'Fixar'}
        </DropdownItem>
      )}
      {onRename && (
        <DropdownItem onClick={onRename}>
          <DropdownItemIcon>
            <FontAwesomeIcon icon={faEdit} />
          </DropdownItemIcon>
          Renomear
        </DropdownItem>
      )}
      {onArchive && (
        <DropdownItem onClick={onArchive}>
          <DropdownItemIcon>
            <FontAwesomeIcon icon={faBoxArchive} />
          </DropdownItemIcon>
          Arquivar
        </DropdownItem>
      )}
      {onRestore && (
        <DropdownItem onClick={onRestore}>
          <DropdownItemIcon>
            <FontAwesomeIcon icon={faBoxOpen} />
          </DropdownItemIcon>
          Restaurar
        </DropdownItem>
      )}
    </DropdownMenu>
  ) : null

  return dropdownContent ? createPortal(dropdownContent, document.body) : null
}
